import { z } from 'zod';
import { isValidAlias, MAX_ASSIGNEES } from '../../../shared/domain/validateAssignee';

/**
 * Zod validation schema for the create-audit form.
 *
 * All fields are required. Assignees must have at least one valid
 * alias. Expiry date must be strictly after the start date. Audit
 * type is validated as a non-empty string (enum enforcement happens
 * at the UI level via disabled options per permission).
 */
export const createAuditFormSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Audit name is required' })
      .max(200, { message: 'Audit name must be at most 200 characters' }),
    location: z.string().min(1, { message: 'Location is required' }),
    divisionName: z.string().min(1, { message: 'Division is required' }),
    templateId: z.string().min(1, { message: 'Template is required' }),
    auditType: z.string().min(1, { message: 'Audit type is required' }),
    startDate: z
      .string()
      .min(1, { message: 'Start date is required' })
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Start date must be YYYY-MM-DD' }),
    expiryDate: z
      .string()
      .min(1, { message: 'Expiry date is required' })
      .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Expiry date must be YYYY-MM-DD' }),
    assignees: z
      .array(z.string())
      .min(1, { message: 'At least one assignee is required' })
      .max(MAX_ASSIGNEES, {
        message: `At most ${MAX_ASSIGNEES} assignees are allowed`,
      })
      .refine((arr) => arr.every((a) => isValidAlias(a)), {
        message: 'All assignees must be valid aliases (alphanumeric only)',
      }),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.expiryDate) return true;
      return data.expiryDate > data.startDate;
    },
    {
      message: 'Expiry date must be after start date',
      path: ['expiryDate'],
    },
  );

/** Inferred form values type after successful validation. */
export type CreateAuditFormValues = z.infer<typeof createAuditFormSchema>;
